import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CartCountBadge } from "@/components/layout/CartCountBadge";
import { MobileNav } from "@/components/layout/MobileNav";
import { useCartItemCount } from "@/lib/cart";
import { getCategoryTree, getPublicSettings, type CategoryTreeNode } from "@/lib/api-client";

interface AnnouncementBar {
  mobile: string;
  desktop: string[];
}

/** Repli identique au contenu historiquement codé en dur, si le setting n'est pas (encore) seedé. */
const DEFAULT_ANNOUNCEMENT_BAR: AnnouncementBar = {
  mobile: "Livraison rapide à Abidjan",
  desktop: ["Livraison rapide à Abidjan et dans les grandes villes", "Paiement à la livraison ou par mobile money"],
};

/**
 * En-tête global (VVS_MODELE_INTERFACE.md §1.3 + navigation §1.4). Porté
 * depuis apps/web/components/layout/Header.tsx (Server Component) — devenu un
 * composant client : les trois chargements (`getCategoryTree()`,
 * `useCartItemCount()`, `getPublicSettings()`) passent par TanStack Query
 * plutôt que par un `Promise.all()` au rendu serveur ; `next/image` → `<img>`,
 * `next/link` → `react-router-dom` `Link`.
 *
 * Rayons de niveau 1 : réels, `getCategoryTree()`. Méga-menu simplifié à une
 * colonne (par rayon : ses sous-rayons directs).
 *
 * Bandeau d'annonce (§1.2, bloc A) : `setting.content.announcement_bar`,
 * éditable depuis `/admin/parametres` sans redéploiement.
 */
export function SiteHeader() {
  const { data: categories } = useQuery({
    queryKey: ["catalog", "category-tree"],
    queryFn: async () => {
      try {
        return await getCategoryTree();
      } catch {
        // apps/api indisponible ou pas encore démarrée : en-tête dégradé (pas de
        // nav de rayons) plutôt qu'un plantage de toute la page.
        return [] as CategoryTreeNode[];
      }
    },
  });
  const topLevel = (categories ?? []).filter((category) => category.isVisible);

  const cartItemCount = useCartItemCount();

  const { data: announcementBar } = useQuery({
    queryKey: ["settings", "announcement-bar"],
    queryFn: loadAnnouncementBar,
  });
  const announcement = announcementBar ?? DEFAULT_ANNOUNCEMENT_BAR;

  return (
    <header className="sticky top-0 z-40">
      {/* Visible à tous les paliers : message unique et condensé sous `lg`, les
          messages complets côte à côte à partir de `lg`. */}
      <div className="bg-gold py-2">
        <p className="container-vvs block text-center font-body text-[11px] font-semibold uppercase tracking-normal text-navy lg:hidden">
          {announcement.mobile}
        </p>
        <div className="container-vvs hidden items-center justify-center gap-6 font-body text-xs font-semibold uppercase tracking-wide text-navy lg:flex">
          {announcement.desktop.map((message, index) => (
            <span key={message} className="flex items-center gap-6">
              {index > 0 ? (
                <span aria-hidden="true" className="opacity-50">
                  |
                </span>
              ) : null}
              {message}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-navy">
        <div className="container-vvs flex flex-col gap-3 py-3 lg:flex-row lg:items-center lg:gap-6 lg:py-4">
          <div className="flex items-center justify-between gap-3 lg:contents">
            <Link to="/" className="shrink-0" aria-label="VVS — Vendez Vouse Store, retour à l'accueil">
              <img
                src="/logo-footer.png"
                alt="VVS — Vendez Vouse Store"
                width={160}
                height={115}
                className="h-10 w-auto lg:h-12"
              />
            </Link>

            <div className="flex items-center gap-3 lg:hidden">
              <Link to="/panier" className="relative text-xl text-cream" aria-label={`Panier (${cartItemCount})`}>
                <span aria-hidden="true">{"\u{1F6D2}"}</span>
                <CartCountBadge count={cartItemCount} tone="gold" className="absolute -right-2 -top-2 h-4 min-w-4 px-1 text-[10px]" />
              </Link>
              <MobileNav categories={topLevel} />
            </div>
          </div>

          <div className="order-3 w-full lg:order-2 lg:flex-1">
            <form
              role="search"
              action="/recherche"
              method="get"
              className="flex w-full items-center overflow-hidden rounded-sm bg-white"
            >
              <label htmlFor="site-search" className="sr-only">
                Rechercher un produit, une marque…
              </label>
              <input
                id="site-search"
                type="search"
                name="q"
                placeholder="Rechercher un produit, une marque…"
                className="w-full bg-transparent px-4 py-2.5 text-sm text-ink placeholder:text-muted focus:outline-none"
              />
              <button
                type="submit"
                aria-label="Rechercher"
                className="flex h-full items-center bg-gold px-4 py-2.5 text-navy transition hover:bg-goldl"
              >
                <span aria-hidden="true">{"\u{1F50D}"}</span>
              </button>
            </form>
          </div>

          <nav
            aria-label="Compte et panier"
            className="order-2 hidden items-center justify-end gap-5 text-sm text-cream lg:order-3 lg:flex"
          >
            <Link to="/compte/favoris" className="flex items-center gap-1.5 hover:text-gold">
              <span aria-hidden="true">{"♡"}</span> Favoris
            </Link>
            <Link to="/compte" className="flex items-center gap-1.5 hover:text-gold">
              <span aria-hidden="true">{"\u{1F464}"}</span> Compte
            </Link>
            <Link
              to="/panier"
              className="flex items-center gap-2 rounded-sm border border-gold/50 bg-gold/20 px-4 py-2 font-semibold text-cream transition hover:bg-gold/30"
            >
              <span aria-hidden="true">{"\u{1F6D2}"}</span> Panier (
              <CartCountBadge
                count={cartItemCount}
                tone="cream"
                hideWhenZero={false}
                className="min-w-[1.25rem] px-1 text-xs align-middle"
              />
              )
            </Link>
          </nav>
        </div>
      </div>

      {topLevel.length > 0 ? (
        <nav aria-label="Rayons" className="hidden border-b border-line bg-white lg:block">
          <ul className="container-vvs flex items-center gap-6 py-2 font-body text-xs font-semibold uppercase tracking-wide text-navy">
            {topLevel.map((category) => (
              <NavItem key={category.id} category={category} />
            ))}
          </ul>
        </nav>
      ) : null}
    </header>
  );
}

function NavItem({ category }: { category: CategoryTreeNode }) {
  const children = category.children.filter((child) => child.isVisible);

  return (
    <li className="group relative">
      <Link to={`/catalogue/${category.slug}`} className="inline-flex items-center gap-1 py-2 hover:text-gold">
        {category.name}
      </Link>
      {children.length > 0 ? (
        <div className="absolute left-0 top-full z-50 hidden min-w-[220px] rounded-sm border border-line bg-white p-3 shadow-lg group-hover:block group-focus-within:block">
          <ul className="space-y-1 font-body text-xs font-normal normal-case text-ink">
            {children.map((child) => (
              <li key={child.id}>
                <Link
                  to={`/catalogue/${category.slug}/${child.slug}`}
                  className="block rounded-sm px-2 py-1 hover:bg-cream2 hover:text-gold"
                >
                  {child.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Repli sur `DEFAULT_ANNOUNCEMENT_BAR` si `content.announcement_bar` n'est pas
 * (encore) seedé ou a une forme inattendue — jamais un bandeau vide ni un
 * plantage de l'en-tête pour un simple texte manquant.
 */
async function loadAnnouncementBar(): Promise<AnnouncementBar> {
  try {
    const settings = await getPublicSettings();
    const raw = settings["content.announcement_bar"];
    if (
      raw &&
      typeof raw === "object" &&
      typeof (raw as { mobile?: unknown }).mobile === "string" &&
      Array.isArray((raw as { desktop?: unknown }).desktop) &&
      (raw as { desktop: unknown[] }).desktop.every((m) => typeof m === "string") &&
      (raw as { desktop: unknown[] }).desktop.length > 0
    ) {
      return raw as AnnouncementBar;
    }
    return DEFAULT_ANNOUNCEMENT_BAR;
  } catch {
    return DEFAULT_ANNOUNCEMENT_BAR;
  }
}
